import React, { useEffect, useId, useRef, useState } from 'react';

import { DropdownOption } from './DropdownOption';
import styles from './style.css';
import { Inline } from '../Inline';
import { Stack } from '../Stack';
import { Icon } from '../Icon';
import SearchIcon from '@/icons/search.svg';
import { intl } from '@/shared/intl';

export function DropdownMenu({
    options,
    value,
    search,
    onSearchChange,
    onSelect,
    onClose,
    searchPlaceholder,
    emptyText,
    className,
    renderOption,
}) {
    const menuId = useId();
    const listboxId = `${menuId}-listbox`;
    const inputRef = useRef(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const searchLabel = searchPlaceholder
        ?? intl.get('shared.dropdown.searchPlaceholder', { defaultMessage: 'Search' });
    const emptyLabel = emptyText
        ?? intl.get('shared.dropdown.emptyResults', { defaultMessage: 'No results' });

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const filtered = options.filter((option) => {
        const haystack = String(option.searchText ?? option.label).toLowerCase();

        return haystack.includes(search.toLowerCase());
    });

    useEffect(() => {
        setHighlightedIndex(-1);
    }, [search]);

    const highlightedOptionId = highlightedIndex >= 0
        ? `${menuId}-option-${highlightedIndex}`
        : undefined;

    useEffect(() => {
        document.getElementById(highlightedOptionId)?.scrollIntoView?.({ block: 'nearest' });
    }, [highlightedOptionId]);

    const selectHighlighted = () => {
        if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
            onSelect(filtered[highlightedIndex].value);
        }
    };

    const handleKeyDown = (event) => {
        const handled = (() => {
            if (event.key === 'Escape') {
                onClose?.();

                return true;
            }

            if (filtered.length === 0) {
                return false;
            }

            switch (event.key) {
            case 'ArrowDown':
                setHighlightedIndex((current) => (
                    current < 0 ? 0 : Math.min(current + 1, filtered.length - 1)
                ));

                return true;
            case 'ArrowUp':
                setHighlightedIndex((current) => {
                    if (current <= 0) {
                        inputRef.current?.focus();

                        return -1;
                    }

                    return current - 1;
                });

                return true;
            case 'Home':
                setHighlightedIndex(0);

                return true;
            case 'End':
                setHighlightedIndex(filtered.length - 1);

                return true;
            case 'Enter':
                if (highlightedIndex >= 0) {
                    selectHighlighted();

                    return true;
                }

                return false;
            default:
                return false;
            }
        })();

        if (handled) event.preventDefault();
    };

    return (
        <Stack className={[styles.menuContainer, className].filter(Boolean).join(' ')}>
            <Inline
                alignY={'center'}
                stretch={true}
                noWrap={true}
                gap={8}
                className={styles.searchContainer}
            >
                <Icon icon={SearchIcon} className={styles.searchIcon} />

                <input
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    aria-expanded="true"
                    aria-controls={listboxId}
                    aria-activedescendant={highlightedOptionId}
                    aria-autocomplete="list"
                    value={search}
                    placeholder={searchLabel}
                    className={styles.searchInput}
                    onChange={(event) => onSearchChange(event.target.value)}
                    onKeyDown={handleKeyDown}
                />
            </Inline>

            <div id={listboxId} className={styles.optionsList} role="listbox">
                {filtered.length === 0 ? (
                    <div className={styles.emptyResults}>{emptyLabel}</div>
                ) : (
                    filtered.map((option, index) => (
                        <DropdownOption
                            key={option.value}
                            id={`${menuId}-option-${index}`}
                            option={option}
                            isSelected={option.value === value}
                            isHighlighted={index === highlightedIndex}
                            onSelect={onSelect}
                            renderOption={renderOption}
                        />
                    ))
                )}
            </div>
        </Stack>
    );
}
